import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { config } from '@accounts/config';
import { Confirmation, Credentials, Legal, Resume, Second } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, Callout, Field, Form } from '@beyond-js/ui/react';
import { NewPassword, Problem, useCountdown, Wordmark } from '@accounts/ui';
import { Footer } from './signin/footer';
import { useMethods } from './signin/methods';

/**
 * Creating an account with an address and a password: a screen of its own, with the person's name and
 * the password rules shown as they type (ACC-09). Nothing still to write is said (D183): while a field is
 * empty Create account waits quietly and a press puts the caret there; an address or a password that is
 * wrong is said in its field.
 *
 * The answer never says whether the address already had an account: a project that confirms addresses
 * says "check your email" either way, and the person continues from the message. Where the sign-in
 * address was going travels in the message's link (`Confirmation`).
 */
export function SignUp() {
	const { text, client, accounts, refresh, language } = usePresence();
	const credentials = useMemo(() => new Credentials(client), [client]);
	const { providers } = useMethods(accounts);
	const [parameters] = useSearchParams();
	const navigate = useNavigate();

	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [busy, setBusy] = useState(false);
	const [failure, setFailure] = useState<unknown>();
	const [sent, setSent] = useState<string>();
	const [invalid, setInvalid] = useState<{ email?: string; password?: string }>({});
	const [waiting, wait] = useCountdown();

	const confirmation = new Confirmation(parameters, config.authority);
	const back = confirmation.address(globalThis.location.origin);

	async function submit() {
		// Every field holds something here: an empty one stopped the submission in `Form`, saying nothing
		const problems: { email?: string; password?: string } = {};
		if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) problems.email = text('error.field.email');
		if ([...password].length < 10) problems.password = text('password.weak.short', { min: 10 });
		setInvalid(problems);
		if (Object.keys(problems).length) return;

		setBusy(true);
		setFailure(undefined);
		try {
			// The versions this screen presented are recorded only when they are the published ones
			const agreements = new Legal(config.legal).published && (providers?.legal?.terms || providers?.legal?.privacy) ? providers?.legal : undefined;
			const answer = await credentials.signup({ email, password, name: name.trim(), locale: language, back, agreements });
			setPassword('');
			if (answer.confirmation) {
				setSent(email);
				wait(Credentials.WAIT);
				return;
			}
			if (answer.factor) {
				Second.keep(answer.factor);
				navigate(`/signin/factor?${parameters}`);
				return;
			}
			const next = Resume.landed(parameters) ?? confirmation.handoff;
			if (next) globalThis.location.href = next;
			else await refresh();
		} catch (error) {
			setPassword('');
			if (Refusal.is(error) && error.code === 'PASSWORD_WEAK') setInvalid({ password: text(error.key, error.values) });
			else setFailure(error);
		} finally {
			setBusy(false);
		}
	}

	async function resend() {
		if (!sent) return;
		setBusy(true);
		try {
			const answer = await credentials.resend(sent, back);
			wait(answer.retry);
		} catch (error) {
			setFailure(error);
			if (Refusal.is(error) && error.retry) wait(error.retry);
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-4 py-10">
			<header className="flex flex-col gap-3">
				<Wordmark label="Accounts" />
				<div>
					<h1 className="text-title-1 text-ink">{text('signup.title')}</h1>
					<p className="mt-1 text-body text-ink-muted">{text('signup.lead')}</p>
				</div>
			</header>

			{failure ? <Problem error={failure} /> : null}

			{sent ? (
				<Callout
					tone="success"
					live
					title={text('signup.sent', { email: sent })}
					body={waiting > 0 ? text('signin.resend.wait', { count: waiting }) : undefined}
					actions={
						<Button variant="secondary" busy={busy} disabled={waiting > 0} onClick={() => void resend()}>
							{text('signin.resend')}
						</Button>
					}
				/>
			) : (
				<Form
					className="flex flex-col gap-4"
					onSubmit={event => {
						event.preventDefault();
						void submit();
					}}
				>
					<Field label={text('signup.name')} hint={text('signup.name.help')}>
						<input type="text" autoComplete="name" maxLength={100} required value={name} onChange={event => setName(event.target.value)} />
					</Field>
					<Field label={text('signin.email')} error={invalid.email}>
						<input type="email" autoComplete="email" required value={email} onChange={event => setEmail(event.target.value)} />
					</Field>
					<NewPassword label={text('signin.password')} value={password} onChange={setPassword} error={invalid.password} required />
					<Button type="submit" variant="primary" busy={busy} labels={{ busy: text('signin.working') }}>
						{text('signup.submit')}
					</Button>
				</Form>
			)}

			<p className="text-center text-body text-ink-muted">
				{text('signup.have')}{' '}
				<Link className="underline" to={`/signin?${parameters}`}>
					{text('signup.signin')}
				</Link>
			</p>
			<Footer presented={Boolean(providers?.legal?.terms || providers?.legal?.privacy)} />
		</div>
	);
}
